import { useCallback, useMemo } from 'react'
import { SectionList } from 'react-native'
import styled from 'styled-components/native'

import { AvailabilitySlot } from '@dialogue/timekeeper'

import Button from 'APP/Converse/Button'
import Typography from 'APP/Converse/Typography'
import I18n, { isTranslationKey } from 'APP/I18n'

import { useDispatch } from 'APP/Hooks/redux'
import Analytics from 'APP/Services/Analytics'
import { providerBookingActions } from 'APP/Store/ProviderBooking'
import { PARTS_OF_DAY } from '../constants'
import { ProviderButton } from './providerButton'

const ListWrapper = styled.View`
  margin: ${({ theme }) => theme.metrics.baseMargin / 2}px 0;
`

const SectionLabel = styled(Typography)`
  margin: ${({ theme }) => theme.metrics.baseMargin / 2}px 0;
`

const ProviderButtonStyled = styled(ProviderButton)`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
`

interface ProviderSection {
  title: string | undefined
  data: AvailabilitySlot[]
}

export interface Props {
  slots: Record<PARTS_OF_DAY, AvailabilitySlot[]>
  onSelectProvider: (item: AvailabilitySlot) => void
  onQuit: () => void
  enableSchedulingLater?: boolean
}

export const ProviderSectionList = ({
  slots,
  onSelectProvider,
  onQuit,
  enableSchedulingLater = true,
}: Props) => {
  const dispatch = useDispatch()

  const sections: ProviderSection[] = useMemo(
    () =>
      Object.entries(slots || {}).reduce((acc, curr) => {
        const [partOfDay, availabilities] = curr
        const titleKey = `AppointmentBooking.${partOfDay.toLowerCase()}`
        if (availabilities.length > 0) {
          acc.push({
            title: isTranslationKey(titleKey) ? I18n.t(titleKey) : undefined,
            data: availabilities,
          })
        }
        return acc
      }, [] as ProviderSection[]),
    [slots]
  )

  const handlePress = useCallback(
    (item: AvailabilitySlot) => {
      onSelectProvider(item)
      dispatch(providerBookingActions.setSelectedProviderId(item.provider_id))
      Analytics.trackEvent('button_click', {
        button_value: 'provider_card',
      })
    },
    [dispatch, onSelectProvider]
  )

  return (
    <ListWrapper>
      <SectionList
        sections={sections}
        keyExtractor={(item) => item.provider_id.toString()}
        renderItem={({ item }) => (
          <ProviderButtonStyled
            providerId={item.provider_id}
            startAt={item.start_at}
            endAt={item.end_at}
            attributes={item.provider_matching_attributes}
            onPress={() => handlePress(item)}
          />
        )}
        renderSectionHeader={({ section: { title } }) => (
          <SectionLabel variant="subheader">{title}</SectionLabel>
        )}
      />
      {enableSchedulingLater && (
        <Button
          variant="tertiary"
          widthVariant="full"
          title={I18n.t('AppointmentBooking.get_help_scheduling_later') || undefined}
          onPress={onQuit}
          analyticsName="providerBooking.nonOfTheseWork"
          small={false}
          testID="GET_HELP_SCHEDULING_LATER_BTN"
        />
      )}
    </ListWrapper>
  )
}
